'use client';

import { useScreenDetect } from '@sgcmpteltd/uikit-next';
import { Pill } from '@sgcmpteltd/uikit-react';
import type { SelectOption } from '@sgcmpteltd/uikit-react/components/select';
import cx from 'clsx';
import Cookies from 'js-cookie';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { ACTION_STATUS_SUCCESS } from '@/actions';
import { getMyAlertData } from '@/actions/new-car/alert-list/get';
import { getCarDetailData } from '@/actions/new-car/car-detail';
import type { CamelCaseCarDetail } from '@/apis/server/new-car/v1/car-detail';
import type {
  CamelCaseCarModelsList,
} from '@/apis/server/new-car/v1/car-models';
import type {
  CamelCaseResponseData as CamelCaseDealerList,
} from '@/apis/server/new-car/v1/dealer-listing';
import type {
  CamelCaseAdAlert,
  CamelCaseCarAlert,
} from '@/apis/server/new-car/v1/my-alert';
import { isCamelCaseCarAlert } from '@/apis/server/new-car/v1/my-alert/utils';
import type {
  AdAlertModalConent,
  CarAlertModalConent,
} from '@/app/(routes)/listing/_components/page-content/alert-modal';
import {
  AlertModal,
} from '@/app/(routes)/listing/_components/page-content/alert-modal';
import type { User } from '@/app/types';
import { NewCarBreadcrumb as Breadcrumb } from '@/components/breadcrumb';
import { DYAds } from '@/components/dy-ads';
import { TextLink } from '@/components/text-link';
import {
  COOKIE_KEY_MAIL,
  COOKIE_KEY_SESSION_ID,
  COOKIE_KEY_USER_ID,
} from '@/configs/cookies';
import { newCarListingLeaderboard } from '@/configs/dynamic-yields/ads/desktop';
import { AUTHORISED_DISTRIBUTORS } from '@/constants/global';
import { willScrollUpOrDown } from '@/utils/app/utils';
import { joinSgcmMsSite, joinSgcmSite } from '@/utils/url/join/public';

import type {
  Index,
} from '../../../_components/page-content/text-indexes-navigator';
import {
  TextIndexesNavigator,
} from '../../../_components/page-content/text-indexes-navigator';
import {
  isTab,
} from '../../../new-car-dealer-list/_components/page-content/utils';
import { breadcrumbList } from './configs';
import {
  PAGE_TITLE_NEW_CAR_PRICE_LIST,
  PILL_ACTIVE_BACKGROUND_COLOR_RED_1,
  PILL_ACTIVE_TEXT_WEIGHT_SEMIBOLD,
  PILL_BACKGROUND_COLOR_GRAY_1,
  TEXT_MONTHS,
} from './constants';
import { PiPriceListModal } from './pi-prcielist-modal';
import { PricelistMenu } from './pricelist-menu';
import styles from './styles.module.scss';
import type { Component, OnClickPiSubmodelMenu, PriceListType } from './types';

export const PageContent: Component = ({
  adList,
  piList,
  myAlerts = null,
}) => {
  const { isDesktop } = useScreenDetect();

  const [selectedTab, setSelectedTab] = useState<PriceListType>('ad');
  const [
    piPriceListData,
    setPiPriceListData,
  ] = useState<CamelCaseCarDetail | null>(null);
  const [
    selectedPiSubmodelId,
    setSelectedPiSubmodelId,
  ] = useState<number | null>(null);
  const [
    selectedPiModelIsShortlisted,
    setSelectedPiModelIsShortlisted,
  ] = useState<boolean>(false);
  const [
    piSubmodelDataIsLoading, setPiSubmodelDataIsLoading,
  ] = useState<boolean>(false);
  const [
    isPiPriceListModalOpen,
    setIsPiPriceListModalOpen,
  ] = useState(false);
  const [
    isAlertModalOpen,
    setIsAlertModalOpen,
  ] = useState(false);
  const [myAlert, setMyAlert] = useState(myAlerts);
  const [
    matchingAdAlert,
    setMatchingAdAlert,
  ] = useState<AdAlertModalConent | null>(null);
  const [
    matchingPiAlert,
    setMatchingPiAlert,
  ] = useState<CarAlertModalConent | null>(null);
  const [selectedAdIndex, setSelectedAdIndex] = useState<Index | null>(null);
  const [selectedPiIndex, setSelectedPiIndex] = useState<Index | null>(null);
  const [isScrollingUp, setIsScrollingUp] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const stickyRef = useRef<HTMLDivElement>(null);

  const user: User = {
    email: Cookies.get(COOKIE_KEY_MAIL) || '',
    userId: Number(Cookies.get(COOKIE_KEY_USER_ID)) || 0,
    sessionId: Cookies.get(COOKIE_KEY_SESSION_ID) || '',
  };

  const textNoPricelist = isDesktop()
    ? 'No price list available'
    : 'No\nprice list';

  const handlePriceListTypeChange = (
    type: PriceListType,
  ) => () => {
    setSelectedTab(type);
    window.location.hash = `#${type}`;
  };

  function generateFormatedDate(date: string): string {
    const [year, month, day] = date.split('-');
    const formattedDate = `${day} ${TEXT_MONTHS[parseInt(month, 10) - 1]} ${year}`;
    return formattedDate;
  }

  function generateAdOptionList(
    dates: string[],
    dealerId: number,
  ): SelectOption[] {
    const optionList = dates.toReversed().map(
      (priceDate, index) => {
        const pdfHref = `${joinSgcmSite(`/new_cars/pricelist/${dealerId}/${priceDate}.pdf`)}`;
        const isLatest = index === 0;
        const formattedDate = generateFormatedDate(priceDate);
        const formattedDateText = isLatest ? `Latest Pricelist (${formattedDate})` : formattedDate;

        return {
          value: pdfHref,
          text: formattedDateText,
        };
      },
    );

    return optionList;
  }

  const handleAdItemClick = useCallback((value?: string | number) => {
    if (typeof value === 'string') {
      const itemUrl = new URL(value);

      window.open(itemUrl, '_blank');
    }
  }, []);

  const getMatchingAlertData = useCallback((selectedPiModel: CamelCaseCarDetail) => { // @suppress longLineCheck
    if (!myAlert) {
      return;
    }

    const matchingCarAlerts = myAlert.carAlerts.filter(
      alert => alert.carId === selectedPiModel.id,
    );

    const carAlertModalContent: CarAlertModalConent = {
      carInfo: {
        id: selectedPiModel?.id,
        make: selectedPiModel?.make,
        model: selectedPiModel?.model,
      },
      carAlert: matchingCarAlerts.length
        ? matchingCarAlerts[0]
        : {
          carId: selectedPiModel.id,
          adCarPriceChange: false,
          piCarPromo: false,
        },
    };
    const adAlertModalContent: AdAlertModalConent = {
      adInfo: {
        id: 0,
        name: '',
        brand: selectedPiModel && selectedPiModel.make,
      },
      adAlert: myAlert.adAlerts[0], // @suppress longLineCheck
    };

    if (matchingCarAlerts.length && matchingCarAlerts[0].piCarPromo) {
      setSelectedPiModelIsShortlisted(true);
    } else {
      setSelectedPiModelIsShortlisted(false);
    }

    setMatchingAdAlert(adAlertModalContent);
    setMatchingPiAlert(carAlertModalContent);
  }, [myAlert]);

  const handlePiItemClick: OnClickPiSubmodelMenu = useCallback((modelId, submodelId) => { // @suppress longLineCheck
    if (!submodelId) {
      return;
    }

    if (!isDesktop()) {
      window.open(
        `${joinSgcmMsSite(`new-cars/pricelists/pi-popup/${modelId}/${submodelId}`)}`,
        '_self',
      );
    }

    setIsPiPriceListModalOpen(true);
    setPiSubmodelDataIsLoading(true);

    const dealerDetailFormData = new FormData();

    dealerDetailFormData.set('id', `${modelId}`);
    getCarDetailData(dealerDetailFormData).then((result) => {
      if (result.status === ACTION_STATUS_SUCCESS) {
        const res = result.data.data;

        setPiPriceListData(res);
        setSelectedPiSubmodelId(Number(submodelId));
        getMatchingAlertData(res);
        setPiSubmodelDataIsLoading(false);
      }
    });
  }, [getMatchingAlertData, isDesktop]);

  const handleAlertClick = () => {
    if (user.sessionId) {
      setIsPiPriceListModalOpen(false);
      setIsAlertModalOpen(true);
    } else {
      window.open(
        `${joinSgcmSite('/member/login')}`,
        '_self',
      );
    }
  };

  const handleSubmitAlert = (
    alert: CamelCaseAdAlert | CamelCaseCarAlert,
  ) => {
    if (isCamelCaseCarAlert(alert) && myAlerts) {
    // remove
      if (!alert.adCarPriceChange && !alert.piCarPromo) {
        const newCarAlert = myAlerts?.carAlerts
          .filter(carAlert => carAlert.carId !== alert.carId);

        if (newCarAlert) {
          setMyAlert({ ...myAlerts, carAlerts: newCarAlert });
        }
      } else { // add
        setMyAlert({
          ...myAlerts,
          carAlerts: [...myAlerts.carAlerts, alert],
        });
      }
    }
  };

  const handleAdIndexOnClick = (index: Index) => {
    setSelectedAdIndex(index);
    const mobileHeaderHeight = 54;
    const indexEl = document.getElementById(index.key);

    if (indexEl && stickyRef.current) {
      window.scrollTo({
        behavior: 'smooth',
        top:
        indexEl.getBoundingClientRect().top
        - document.body.getBoundingClientRect().top
        - stickyRef.current.getBoundingClientRect().height
        - (willScrollUpOrDown(indexEl) === 'up' && !isDesktop()
          ? mobileHeaderHeight
          : 0),
      });
    }
  };

  const handlePiIndexOnClick = (index: Index) => {
    setSelectedPiIndex(index);
    const mobileHeaderHeight = 54;
    const indexEl = document.getElementById(index.key);

    if (indexEl && stickyRef.current) {
      window.scrollTo({
        behavior: 'smooth',
        top:
        indexEl.getBoundingClientRect().top
        - document.body.getBoundingClientRect().top
        - stickyRef.current.getBoundingClientRect().height
        - (willScrollUpOrDown(indexEl) === 'up' && !isDesktop()
          ? mobileHeaderHeight
          : 0),
      });
    }
  };

  const generateIndexes = (data: CamelCaseDealerList) => {
    const groupedBrands = data.reduce<{ [key: string]: boolean; }>((acc, item) => { // @suppress longLineCheck
      const firstLetter = item.brand?.[0]?.toUpperCase();

      if (firstLetter && firstLetter >= 'A' && firstLetter <= 'Z') {
        return { ...acc, [firstLetter]: true };
      }

      return acc;
    }, {});

    return Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))
      .filter(letter => groupedBrands[letter])
      .map(letter => ({
        key: letter,
        displayValue: letter,
      }));
  };

  const generatePiIndexes = (data: CamelCaseCarModelsList) => Array.from(
    new Set(data.map(item => item.make?.charAt(0).toUpperCase() || '')),
  )
    .filter(letter => letter !== '')
    .sort()
    .map(letter => ({
      key: /^[A-Za-z]$/.test(letter) ? letter : '#',
      displayValue: /^[A-Za-z]$/.test(letter) ? letter : '#',
    }));

  const adIndexes = generateIndexes(adList);
  const piIndexes = generatePiIndexes(piList);

  useEffect(() => {
    if (myAlerts === null) {
      getMyAlertData().then((result) => {
        if (result.status === ACTION_STATUS_SUCCESS) {
          const res = result.data.data;

          setMyAlert(res);
        }
      });
    }
  }, [myAlerts]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      setIsScrollingUp(currentScrollY < lastScrollY);
      setLastScrollY(currentScrollY);

      if (stickyRef.current) {
        const rect = stickyRef.current.getBoundingClientRect();

        if (rect.top <= (isScrollingUp ? 54 : 0)) {
          setIsSticky(true);
        } else {
          setIsSticky(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, isScrollingUp]);

  useEffect(() => {
    const toggleTab = () => {
      if (isTab(window.location.hash.slice(1))) {
        setSelectedTab(
          window.location.hash.slice(1) as PriceListType,
        );
      } else {
        setSelectedTab(AUTHORISED_DISTRIBUTORS);
      }
    };

    toggleTab();
    window.addEventListener('hashchange', toggleTab);
    return () => window.removeEventListener('hashchange', toggleTab);
  }, []);

  return (

    <main
      className={styles.containerPageRoot}>
      {matchingPiAlert && matchingAdAlert
      && (
        <AlertModal
          adAlertModalContent={matchingAdAlert}
          carAlertModalContent={matchingPiAlert}
          isOpen={isAlertModalOpen}
          type="piAlert"
          user={user}
          onClose={() => {
            setIsAlertModalOpen(false);
          }}
          onSubmit={handleSubmitAlert} />
      )}
      <PiPriceListModal
        isLoading={piSubmodelDataIsLoading}
        isOpen={isPiPriceListModalOpen}
        modelIsShortlisted={selectedPiModelIsShortlisted}
        piPriceListData={piPriceListData}
        submodelId={selectedPiSubmodelId}
        onClose={() => {
          setIsPiPriceListModalOpen(false);
          setPiPriceListData(null);
        }}
        onOpenAlertModal={handleAlertClick} />
      <div className={styles.pageContainerRoot}>
        <div className={styles.containerLeaderboard}>
          <DYAds
            smartObjectId={newCarListingLeaderboard.smartObjectId}
            target={newCarListingLeaderboard.target} />
        </div>
        <div className={styles.containerBreadcrumb}>
          <Breadcrumb
            list={breadcrumbList} />
        </div>
        <div className={styles.containerTitle}>
          <h1 className={styles.textTitle}>
            {PAGE_TITLE_NEW_CAR_PRICE_LIST}
          </h1>
        </div>
        <div className={styles.containerPricelitsTypeTabs}>
          <Pill
            activeBackgroundColor={PILL_ACTIVE_BACKGROUND_COLOR_RED_1}
            activeTextColor={PILL_ACTIVE_BACKGROUND_COLOR_RED_1}
            activeTextWeight={PILL_ACTIVE_TEXT_WEIGHT_SEMIBOLD}
            backgroundColor={PILL_BACKGROUND_COLOR_GRAY_1}
            className={cx(
              styles.pill,
              styles.containerPricelistTypePill,
            )}
            hoverTextColor={PILL_ACTIVE_BACKGROUND_COLOR_RED_1}
            isActive={selectedTab === 'ad'}
            textColor={PILL_BACKGROUND_COLOR_GRAY_1}
            onClick={handlePriceListTypeChange('ad')}>
            <span className={styles.textPillTitle}>
              Authorised Distributor
            </span>
          </Pill>
          <Pill
            activeBackgroundColor={PILL_ACTIVE_BACKGROUND_COLOR_RED_1}
            activeTextColor={PILL_ACTIVE_BACKGROUND_COLOR_RED_1}
            activeTextWeight={PILL_ACTIVE_TEXT_WEIGHT_SEMIBOLD}
            backgroundColor={PILL_BACKGROUND_COLOR_GRAY_1}
            className={cx(
              styles.pill,
              styles.containerPricelistTypePill,
            )}
            hoverTextColor={PILL_ACTIVE_BACKGROUND_COLOR_RED_1}
            isActive={selectedTab === 'pi'}
            textColor={PILL_BACKGROUND_COLOR_GRAY_1}
            onClick={handlePriceListTypeChange('pi')}>
            <span className={styles.textPillTitle}>
              Parallel Importer
            </span>
          </Pill>
        </div>
      </div>
      <div className={styles.containerTextIndexNavigatorPadding} />
      {selectedTab === 'ad'
        ? (
          <div
            ref={stickyRef}
            className={cx(
              styles.containerTextIndexNavigator,
              isSticky && styles.sticky,
              isScrollingUp && styles.scrollingUp,
            )}>
            <div className={styles.pageContainerRoot}>
              <div className={styles.containerTextIndexNavigatorInner}>
                <TextIndexesNavigator
                  indexes={adIndexes}
                  scrollDistance={({ clientWidth }) => clientWidth}
                  selected={selectedAdIndex || undefined}
                  onIndexClick={handleAdIndexOnClick} />
              </div>
            </div>
          </div>
        )
        : (
          <div
            ref={stickyRef}
            className={cx(
              styles.containerTextIndexNavigator,
              isSticky && styles.sticky,
              isScrollingUp && styles.scrollingUp,
            )}>
            <div className={styles.pageContainerRoot}>
              <div className={styles.containerTextIndexNavigatorInner}>
                <TextIndexesNavigator
                  indexes={piIndexes}
                  scrollDistance={({ clientWidth }) => clientWidth}
                  selected={selectedPiIndex || undefined}
                  onIndexClick={handlePiIndexOnClick} />
              </div>
            </div>
          </div>
        )}
      <div className={styles.containerTextIndexNavigatorPadding} />
      <div className={styles.pageContainerRoot}>
        <div className={styles.containerPricelistTable}>
          <div className={cx(
            styles.containerTableTitle,
            styles.containerTableRow,
          )}>
            <div className={styles.containerCell}>By Car Make</div>
            {selectedTab === 'ad'
            && (
              <div className={cx(
                styles.containerCell,
                styles.isInDesktop,
              )}>
                Authorised Distributor
              </div>
            )}
            <div className={cx(
              styles.containerCell,
              styles.isInDesktop,
              styles.isFixedWidthCell,
            )}>
              Pricelist
            </div>
          </div>
          { selectedTab === 'ad' && (
            <div className={styles.containerList}>
              {adList.map((ad, index, array) => {
                const {
                  id, brand, name, pricelistDates, isCommercial,
                } = ad;
                const indexId = array[
                  index - 1 >= 0 ? index - 1 : array.length - 1
                ].brand?.[0].toUpperCase() === brand?.[0].toUpperCase()
                  ? undefined
                  : brand?.[0].toUpperCase();
                const fullBrandName = isCommercial ? `${brand} (Commercial) Pricelist` : `${brand} Pricelist`;

                return (
                  <div
                    key={`ad-${id}`}
                    className={styles.containerTableRow}
                    id={indexId}>
                    <div className={styles.containerCell}>
                      <TextLink
                        className={styles.textPricelistContent}
                        href={`/new-cars/pricelists/${id}/${brand?.toLowerCase()}`}
                        text={fullBrandName} />
                    </div>
                    <div className={cx(
                      styles.containerCell,
                      styles.isInDesktop,
                    )}>
                      <TextLink
                        className={styles.textPricelistContent}
                        href={`${joinSgcmSite(`/new_cars/newcars_listing.php?DID=${id}`)}`}
                        text={`${name}`} />
                    </div>
                    <div className={styles.isFixedWidthCell}>
                      {pricelistDates.length > 0
                        ? (
                          <div>
                            <PricelistMenu
                              menuName="Pricelist"
                              optionList={generateAdOptionList(
                                pricelistDates,
                                id,
                              )}
                              pricelistType="ad"
                              onItemClick={handleAdItemClick} />
                          </div>
                        )
                        : (
                          <div className={styles.textNoPricelist}>
                            <span className={styles.textNoPricelistContent}>
                              {textNoPricelist}
                            </span>
                          </div>
                        )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          { selectedTab === 'pi' && (
            <div className={styles.containerList}>
              {piList.map((pi, index, array) => {
                const {
                  id, make, model, subModels,
                } = pi;
                const indexId = array[
                  index - 1 >= 0 ? index - 1 : array.length - 1
                ].make?.[0].toUpperCase() === make?.[0].toUpperCase()
                  ? undefined
                  : make?.[0].toUpperCase();

                return (
                  <div
                    key={`ad-${id}`}
                    className={styles.containerTableRow}
                    id={indexId}>
                    <div className={styles.containerCell}>
                      <TextLink
                        className={styles.textPricelistContent}
                        href={`${joinSgcmSite(`/new_cars/newcars_overview.php?CarCode=${id}`)}`}
                        text={`${make} ${model}`} />
                    </div>
                    <div className={styles.isFixedWidthCell}>
                      {subModels.length > 0
                        ? (
                          <div>
                            <PricelistMenu
                              menuName="Submodel Pricelist"
                              optionList={
                                subModels.map(({
                                  id: value,
                                  name: text,
                                }) => ({
                                  value,
                                  text,
                                }))
                              }
                              pricelistType="pi"
                              onItemClick={
                                submodelId => handlePiItemClick(id, submodelId)
                              } />
                          </div>
                        )
                        : (
                          <div className={styles.textNoPricelist}>
                            <span className={styles.textNoPricelistContent}>
                              {textNoPricelist}
                            </span>
                          </div>
                        )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </main>
  );
};
